import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, watch } from "vue";
  import Modal from "@/components/ui/Modal.vue";
  import DatePicker from "@/components/ui/DatePicker.vue";
  import Cleave from "vue-cleave-component";
  import useVuelidate from "@vuelidate/core";
  import { requiredIf, numeric } from "@vuelidate/validators";
  import dayjs from "dayjs";
  import { useStore } from "vuex";
  import { LotStatus } from "@/enums/LotStatus";
  import plural from "@/features/plural";
  import { getChangedFields } from "@/helpers/getChangedFields";
  import { useToast } from "vue-toastification";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'EditLotModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    lot: {
      type: Object,
      required: true,
    },
    eois: {
      type: Object,
    },
  },
  emits: ["close", "update", "loading", "offLoading"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const store = useStore();
  const emits = __emit;
  const props = __props;
  const optionPrice = {
    numeral: true,
    numeralThousandsGroupStyle: "thousand",
  };
  const minDate = dayjs().format("YYYY-MM-DD");
  const maxDate = dayjs().add(2, "year").format("YYYY-MM-DD");
  const settlementMaxDate = dayjs().add(30, "year").format("YYYY-MM-DD");
  const toast = useToast();
  const requiredPrice = computed(() => {
    return (
      props.lot &&
      (props.lot.status === LotStatus.NOT_RELEASED ||
        props.lot.status === LotStatus.AVAILABLE ||
        props.lot.status === LotStatus.OFFLINE)
    );
  });
  const requiredDeposit = computed(() => {
    return props.lot && props.lot.status === LotStatus.OFFLINE;
  });

  const availableLotWithApplicants = computed(() => {
    return (
      props.lot.status === LotStatus.AVAILABLE && props.lot?.applicants.length
    );
  });

  const checkSubjectToFinance = computed(() => {
    return (
      props.lot?.eoiId &&
      props.lot?.eoiId?.contractConditions &&
      props.lot?.eoiId?.contractConditions?.subjectToFinance
    );
  });

  const form = reactive({
    price: props.lot?.price,
    streetName: props.lot?.streetName,
    deposit: props.lot?.offlineSale?.offlineDeposit / 100,
    offlineBuyer: props.lot?.offlineSale?.offlineBuyerData?.fullName,
    registrationDate: props.lot?.registrationDate,
    financeDueDate: props.lot?.financeDueDate,
    settlementDate: props.lot?.settlementDate,
  });
  const rules = {
    price: { requiredIf: requiredIf(requiredPrice), numeric },
    deposit: { numeric },
  };
  const v$ = useVuelidate(rules, form);

  watch(
    () => props.lot,
    () => {
      form.price = props.lot.price;
      form.streetName = props.lot.streetName;
      form.deposit = props.lot?.offlineSale?.offlineDeposit / 100;
      form.offlineBuyer = props.lot?.offlineSale?.offlineBuyerData?.fullName;
      form.registrationDate = props.lot.registrationDate;
      form.financeDueDate = props.lot.financeDueDate;
      form.settlementDate = props.lot.settlementDate;
    }
  );
  function close() {
    emits("close");
  }
  const edit = () => {
    if (!requiredDeposit.value) {
      form.deposit = 0;
    }

    v$.value.$touch();

    if (v$.value.$invalid) {
      return;
    }

    const normalizedData: any = getNormalizedData();

    if (props.lot.status !== LotStatus.OFFLINE) {
      delete normalizedData.offlineSale.offlineDeposit;
    }

    const data = getChangedFields(normalizedData, props.lot);

    if (availableLotWithApplicants.value) {
      delete data.offlineSale;
    }

    close();

    if (props.lot.status === LotStatus.OFFLINE) {
      if (
        props.lot?.offlineSale?.offlineBuyerData?.fullName !== form.offlineBuyer
      ) {
        data.offlineSale = {
          offlineBuyerData: {
            fullName: form.offlineBuyer,
          },
        };
      }

      if (
        props.lot?.offlineSale?.offlineDeposit !==
        Number(form.deposit) * 100
      ) {
        Object.assign(data.offlineSale, {
          offlineDeposit: form.deposit * 100,
        });
      } else {
        if (typeof data.offlineSale?.offlineBuyerData?.fullName === "string") {
          delete data.offlineSale.offlineDeposit;
        } else {
          delete data.offlineSale;
        }
      }

      emits("loading");
      store
        .dispatch("lot/updateOffline", { id: props.lot._id, data })
        .then(() => {
          emits("update");
        })
        .catch(() => {
          form.streetName = props.lot.streetName;
          toast.error("Something went wrong. Please reload the page");
          emits("offLoading");
        });
      return;
    }

    if (Object.keys(data).length) {
      emits("loading");
      store
        .dispatch("lot/updateLot", { id: props.lot._id, data })
        .then(() => {
          emits("update");
        })
        .catch(() => {
          form.price = props.lot.price;
          form.streetName = props.lot.streetName;
          toast.error("Something went wrong. Please reload the page");
          emits("offLoading");
        });
    }
  };

  const setFormToDefaultAndClose = () => {
    form.price = props.lot?.price;
    form.streetName = props.lot?.streetName;
    form.deposit = props.lot?.offlineSale?.offlineDeposit / 100;
    form.offlineBuyer = props.lot?.offlineSale?.offlineBuyerData?.fullName;
    form.registrationDate = props.lot?.registrationDate;
    form.financeDueDate = props.lot?.financeDueDate;
    form.settlementDate = props.lot?.settlementDate;
    close();
  };

  const getNormalizedData = () => {
    return {
      price: Number.parseInt(form.price),
      streetName: form.streetName,
      registrationDate: dayjs(form.registrationDate).isValid()
        ? dayjs(form.registrationDate).toISOString()
        : form.registrationDate,
      financeDueDate: dayjs(form.financeDueDate).isValid()
        ? dayjs(form.financeDueDate).toISOString()
        : form.financeDueDate,
      settlementDate: dayjs(form.settlementDate).isValid()
        ? dayjs(form.settlementDate).toISOString()
        : form.settlementDate,
      offlineSale: {
        offlineDeposit: form.deposit * 100,
      },
    };
  };

  const isLotStatusEditable = computed(
    () =>
      props.lot.status === LotStatus.NOT_RELEASED ||
      props.lot.status === LotStatus.OFFLINE ||
      props.lot.status === LotStatus.AVAILABLE
  );

const __returned__ = { store, emits, props, optionPrice, minDate, maxDate, settlementMaxDate, toast, requiredPrice, requiredDeposit, availableLotWithApplicants, checkSubjectToFinance, form, rules, v$, close, edit, setFormToDefaultAndClose, getNormalizedData, isLotStatusEditable, computed, reactive, watch, Modal, DatePicker, get Cleave() { return Cleave }, get useVuelidate() { return useVuelidate }, get requiredIf() { return requiredIf }, get numeric() { return numeric }, get dayjs() { return dayjs }, get useStore() { return useStore }, get LotStatus() { return LotStatus }, get plural() { return plural }, get getChangedFields() { return getChangedFields }, get useToast() { return useToast } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})